// Colors for the risk-description charts (UI only).
//
// The fire map layer paints land-cover polygons straight from a per-feature
// `color` property, so the frontend has no `class_id -> color` lookup. We rebuild
// one here from the fire legend in `risksMap/mapMenu.js`, matching each fuel
// class_id to the legend entry with the same name. Only the vegetation / fuel
// classes appear in `class_breakdown`; anything else falls back to neutral gray.
import {
  EARTHQUAKE_HAZARD_COLOR_STOPS,
  FLOOD_HAZARD_COLOR_STOPS_Z13,
} from "../../risksMap/constants";

const NEUTRAL_COLOR = "#BDBDBD";

// class_id -> hex, derived from the ordered fire legend colors in mapMenu.js.
export const FIRE_CLASS_COLORS = {
  2: "#228B22", // Woody needle leaved trees (Conifer)
  3: "#80FF00", // Woody broadleaved deciduous trees
  4: "#00FF08", // Woody broadleaved evergreen trees
  5: "#804000", // Low-growing woody plants (Shrub)
  6: "#CCF24D", // Permanent herbaceous (Grass)
  7: "#FFFF80", // Periodically herbaceous (Crops)
  8: "#FF80FF", // Lichens & mosses
};

export const getFireClassColor = (classId) =>
  FIRE_CLASS_COLORS[Number(classId)] || NEUTRAL_COLOR;

// Buckets without a severity (uncovered / out_zone / missing) render neutral gray.
const NEUTRAL_BUCKETS = new Set(["uncovered", "out_zone", "missing"]);

// Map explicit severity names to their corresponding color-stop indices for each hazard type.
// For flood, this ensures each bucket gets a distinct stop: "low" uses the greenest (first) stop,
// and higher severities use deeper shades. Without this mapping, some buckets would otherwise share
// colors due to an uneven distribution of severities versus available stops.
const FLOOD_BUCKET_COLOR_INDEX = {
  low: 0,
  medium: 1,
  elevated: 2,
  high: 3,
};
const EARTHQUAKE_BUCKET_COLOR_INDEX = {
  very_low: 0,
  low: 1,
  medium: 2,
  elevated: 3,
  high: 4,
  very_high: 5,
};

// Map a zone-coverage bucket to a color sampled from the risk's hazard gradient,
// so the pie matches the colors used on the map.
export const getZoneCoverageColor = (bucket, riskType) => {
  const key = String(bucket || "").toLowerCase();
  if (NEUTRAL_BUCKETS.has(key)) return NEUTRAL_COLOR;

  const isFlood = riskType === "flood";
  const colors = (
    isFlood ? FLOOD_HAZARD_COLOR_STOPS_Z13 : EARTHQUAKE_HAZARD_COLOR_STOPS
  ).map(([, color]) => color);
  const colorIndex = (
    isFlood ? FLOOD_BUCKET_COLOR_INDEX : EARTHQUAKE_BUCKET_COLOR_INDEX
  )[key];

  // in_zone (and any covered-but-unranked bucket) maps to the most severe color.
  return colors[colorIndex ?? colors.length - 1];
};
